import { useId, useState, type ReactElement, type ReactNode } from 'react';
import { AlertIcon, ChevronIcon, PencilIcon, TrashIcon } from '@/components/catalog/icons';
import { TaskAttachmentsPanel } from '@/components/tasks/TaskAttachmentsPanel';
import { dueChip } from '@/components/tasks/task-due-chip';
import { ExternalTabIcon } from '@/components/tracker-sheet/OpenTrackerSheet';
import {
  Avatar,
  Badge,
  Button,
  ConfirmDialog,
  EmptyState,
  Skeleton,
  Tooltip,
} from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { cx } from '@/lib/cx';
import { formatDateOnly, formatDateTime } from '@/lib/format';
import {
  formatDue,
  isForeignZone,
  isOverdue,
  relativeDue,
  taskKindLabel,
  taskStateLabel,
} from '@/lib/task-time';
import type {
  EnquirySource,
  Task,
  TaskOrigin,
  TaskState,
  TaskTarget,
  TaskTargetType,
} from '@/lib/types';

/*
 * The task list, as it appears on a record panel and on the dashboard.
 *
 * Presentational by design: it owns no fetch and no mutation. Completion,
 * editing and deletion are all handed up, because the same list renders
 * inside a deal, a quote and a day brief, each already owning the rows it
 * shows — a component that mutated them would leave three caches disagreeing
 * about what is still open.
 *
 * Two rules here are contract rather than taste: who may delete, and whose
 * clock a due time is on.
 */

/**
 * The day a snoozed task (`availableFrom` still in the future) comes back,
 * or `null` when it is already visible / the server does not report it.
 *
 * ⚠️ The instant is 00:00 on the ASSIGNEE's clock, so the day is read in the
 * due zone (else the viewer's) — slicing the UTC string would land a day early
 * for anyone east of Greenwich.
 */
function availableFromLabel(task: Task, now: Date | undefined, viewerTimeZone: string): string | null {
  if (!task.availableFrom) return null;
  const at = Date.parse(task.availableFrom);
  if (Number.isNaN(at) || at <= (now ?? new Date()).getTime()) return null;
  try {
    const day = new Intl.DateTimeFormat('en-CA', {
      timeZone: task.due?.timeZone ?? viewerTimeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(at);
    return formatDateOnly(day);
  } catch {
    return formatDateTime(task.availableFrom);
  }
}

/* --- icons --------------------------------------------------------------- */

const stroke = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} as const;

function CheckIcon() {
  return (
    <svg {...stroke} strokeWidth={2.5} className="h-3 w-3">
      <path d="m5 12 5 5L20 7" />
    </svg>
  );
}

function ClockIcon() {
  return (
    <svg {...stroke} className="h-3.5 w-3.5 shrink-0">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

function PaperclipIcon() {
  return (
    <svg {...stroke} className="h-3.5 w-3.5 shrink-0">
      <path d="m21 11-8.6 8.6a5 5 0 0 1-7-7l8.6-8.6a3.3 3.3 0 0 1 4.7 4.7l-8.6 8.6a1.7 1.7 0 0 1-2.4-2.4L15.6 7" />
    </svg>
  );
}

/** Empty-state glyph — a checklist, so tasks read apart from notes at a glance. */
function TaskIcon() {
  return (
    <svg {...stroke} className="h-5 w-5">
      <path d="m3 6 2 2 3-3" />
      <path d="m3 14 2 2 3-3" />
      <path d="M12 7h9" />
      <path d="M12 15h9" />
    </svg>
  );
}

/* --- helpers ------------------------------------------------------------- */

/**
 * Where a task's target record can be opened — `null` when it cannot be.
 *
 * ⚠️ **`deliverable` is `null` because `/deliverables/:id` no longer exists.**
 * The old hand-entered entity was deleted; its API resource, and every task
 * already attached to one, are still there. So the target keeps its NOUN and
 * loses only its LINK, and the row still says what the task is about.
 *
 * ⚠️ **The type is `string | null` rather than the key being removed, and that
 * is the whole guard.** `Record<TaskTargetType, …>` is total, so a member of
 * `TaskTargetType` with no entry is a compile error — which is what forces this
 * decision to be made rather than defaulted. Deleting the key would have meant
 * narrowing `TaskTargetType` itself, and that type mirrors what the SERVER
 * accepts: it still takes `deliverable`, and a task carrying one would then
 * render `undefined/12` in an href. Nothing would throw.
 */
const TARGET_PATH: Record<TaskTargetType, string | null> = {
  deal: '/deals',
  quote: '/quotes',
  invoice: '/invoices',
  deliverable: null,
  company: '/companies',
};

const TARGET_NOUN: Record<TaskTargetType, string> = {
  deal: 'Deal',
  quote: 'Quote',
  invoice: 'Invoice',
  deliverable: 'Deliverable',
  company: 'Company',
};

/** `Company #622` — the unambiguous reference, kept on the `title`. */
function targetRef(target: TaskTarget): string {
  return `${TARGET_NOUN[target.type]} #${target.id}`;
}

/**
 * What the task is about, by NAME (owner's request 2026-09-24): the company
 * name, deal name, or quote/invoice subject the server resolves. Falls back to
 * the noun and id when no name comes back — a deliverable, a deleted record, or
 * an older server that does not send `name`.
 */
function targetLabel(target: TaskTarget): string {
  const name = target.name?.trim();
  return name ? name : targetRef(target);
}

/**
 * The Enquiries page tab each enquiry source lives on (`?tab=` values from
 * `pages/EventInterestPage.tsx`). Total over `EnquirySource`, so a new source
 * is a compile error here rather than a link to the default tab.
 */
const ENQUIRY_TAB: Record<EnquirySource, string> = {
  'event-interest': 'submissions',
  'b2b-proposal': 'proposals',
  'report-download': 'reports',
};

/** The follow-up link on a task an enquiry assignment created (owner, 2026-10-16). */
function EnquiryLink({ enquiry }: { enquiry: NonNullable<Task['enquiry']> }) {
  return (
    <TextLink
      to={`/event-interest?tab=${ENQUIRY_TAB[enquiry.source]}`}
      title={`Enquiry #${enquiry.id}`}
    >
      Enquiry
    </TextLink>
  );
}

/** A closed task is `done` or `cancelled` — both are treated as closed. */
function isClosed(state: TaskState): boolean {
  return state !== 'open';
}

/**
 * Marks a task that was BORN in ITSM. Renders nothing for one born here.
 *
 * The owner asked for provenance to be visible precisely so a reader can tell
 * the two apart, so it is spent in ONE place — a chip on the row — and nowhere
 * else. No column, no banner, no second mention in the expanded panel at the
 * widths where the row already carries it.
 *
 * ⚠️ **`'emerge'` RENDERS NOTHING, AND THAT ASYMMETRY IS THE WHOLE REASON THIS
 * FITS ON THE ROW.** A chip on every task would be a fixed width cost on every
 * row for a fact that is the same on most of them; a chip only where the answer
 * is the unusual one costs width exactly where it carries information. Same
 * shape as `ImportedFromXeroBadge`, which renders `null` for everything created
 * in EMERGE rather than a "Created here" counterpart.
 *
 * ⚠️ **`neutral`/`outline`, matching "From Xero", and NOT a warning tone.** A
 * task that came from ITSM is not a problem and must not look like one. A
 * `warning` beside the genuine `cancelled` warning on the same row would make
 * the two indistinguishable.
 *
 * ⛔ **It says nothing about SYNC STATE and must not be made to.** Where the
 * task was born and whether its counterpart is in conflict are two different
 * facts — `Task.itsm.status` is the second one, and folding it in here would
 * put a provenance chip's words on a sync refusal.
 *
 * ⚠️ **`className` GOES ON A WRAPPER SPAN AND IS NEVER FORWARDED TO `Badge`,
 * because its callers pass responsive DISPLAY classes.** `Badge` bakes in
 * `inline-flex`, so a `hidden sm:inline-flex` handed to it would put two
 * display utilities on one element and let the emitted stylesheet's order
 * decide which wins — the exact race `Badge`'s own `wrap` prop exists to avoid,
 * and the reason that is a prop rather than a class at the call site. The
 * wrapper carries no baked display class, so there is nothing to race.
 */
function OriginBadge({ origin, className }: { origin: TaskOrigin; className?: string }) {
  if (origin !== 'itsm') return null;
  return (
    <span className={className}>
      <Badge tone="neutral" variant="outline">
        From ITSM
      </Badge>
    </span>
  );
}

/**
 * A sync CONFLICT, which is the one ITSM state that requires a person to decide
 * something — owner's decision, 2026-10-14.
 *
 * 🔴 **WHAT IT FIXES: THE ONE STATE NEEDING A HUMAN WAS THE ONE STATE NO HUMAN
 * WAS SHOWN.** Measured read-only on 2026-10-14 while driving the sync in both
 * directions: a conflicted task's collapsed row, its expanded panel and the
 * whole page contained **no occurrence of the word**, and the row was
 * indistinguishable from an ordinary open task. The fact was already on the wire
 * the entire time (`task.itsm.status`, plus `conflictObservedAt`), and
 * `GET /itsm/status` reported `conflict: 1` — but that is **admin-only and a
 * COUNT naming no task**, so finding *which* task needed a database read. The
 * person who owns the task could not see it at all.
 *
 * ⚠️ **WHY IT MATTERS MORE THAN AN ORDINARY BADGE:** a conflict is not a slow
 * sync. Both sides changed, EMERGE applied nothing in either direction, and
 * **nothing will sync for that task again until somebody resolves it** — so an
 * unseen conflict is a task silently frozen while looking completely healthy.
 *
 * ⛔ **A SEPARATE COMPONENT FROM `OriginBadge`, DELIBERATELY, AND THEY MUST NOT
 * BE MERGED.** That one's docblock already rules on this: it *"says nothing
 * about SYNC STATE and must not be made to… folding it in here would put a
 * provenance chip's words on a sync refusal."* Where a task was BORN and whether
 * its counterpart is STUCK are two different facts, and a task can carry either
 * without the other — an EMERGE-born task can conflict, and most ITSM-born tasks
 * never will.
 *
 * ⚠️ **`danger`, NOT `warning`, AND THAT IS FORCED RATHER THAN CHOSEN.**
 * `cancelled` already renders `<Badge tone="warning">` on this very row, and
 * `OriginBadge`'s docblock warns that a second `warning` beside it *"would make
 * the two indistinguishable"*. A cancelled task is a settled outcome; a
 * conflicted one is unresolved work. They must not read alike.
 *
 * ⚠️ **It renders ONLY for `'conflict'`, never for `failed` or `orphaned`.**
 * Those are different facts with different remedies — a failed push retries, and
 * an orphaned link means the counterpart is gone — and neither is a decision
 * this reader can take. Widening the predicate would turn a specific, actionable
 * marker into a general "something is off", which is the thing nobody acts on.
 *
 * ⚠️ **`className` goes on a WRAPPER SPAN and is never forwarded to `Badge`**,
 * for the reason `OriginBadge` gives in full: `Badge` bakes in `inline-flex`, so
 * a responsive display class handed to it would put two display utilities on one
 * element and let stylesheet order decide the winner.
 *
 * ⛔ **`title` CARRIES THE DATE AND NOTHING ELSE, AND THAT IS A RULE RATHER THAN
 * A CHOICE.** `Badge`'s own prop docblock says it is *"not a home for helper
 * text — the standing prohibition covers advice and explanation wherever it
 * renders, and a `title` is not an exemption"*, and that it is **invisible on
 * touch, so nothing a reader NEEDS may live there alone.** A first draft of this
 * component put a sentence of explanation there, which is exactly what that
 * forbids. What belongs is the value a reader would otherwise go and look up:
 * when the conflict was first seen. ⚠️ **With no observed time there is NO
 * `title` at all** — an empty tooltip and a sentence of advice are the two wrong
 * answers, and the absence is the right one.
 *
 * ⚠️ **So the chip's LABEL has to carry everything essential**, since the title
 * reaches no touch reader. "Sync conflict" plus the `danger` tone is the whole
 * message; what to DO about it belongs on a screen that can hold a sentence,
 * not on a 320px row.
 */
function SyncConflictBadge({
  itsm,
  className,
}: {
  itsm: Task['itsm'];
  className?: string;
}) {
  if (itsm?.status !== 'conflict') return null;

  const observed = itsm.conflictObservedAt;
  return (
    <span className={className}>
      <Badge tone="danger" {...(observed === null ? {} : { title: observed })}>
        {/* ⚠️ SHORTER BELOW `sm`, AND THIS IS A MEASUREMENT RATHER THAN A
            PREFERENCE. Driven at 320px on 2026-10-14, the full label left a
            conflict-only row **39px of a 261px title — 15%**. "Conflict" is
            ~28px narrower and buys that back. Both words are in the DOM and
            exactly one is displayed, so the accessible name still carries a
            single label: `display:none` content is excluded from it. */}
        <span className="sm:hidden">Conflict</span>
        <span className="hidden sm:inline">Sync conflict</span>
      </Badge>
    </span>
  );
}

/**
 * Where this task's ITSM counterpart can be OPENED — `null` when it should not
 * be offered, which is most tasks.
 *
 * ⚠️ **TWO QUESTIONS, ANSWERED IN TWO PLACES, and the split is still the
 * design — but the first place is now the SERVER.** *SUPERSEDED: "`itsmTaskUrl`
 * answers can a correct URL be built — configuration and the id, and it stays
 * status-blind", and `lib/itsm.ts`, which held it, is deleted.* The server
 * answers *can a correct URL be built*, from **`ITSM_APP_BASE_URL`** (corrected
 * 2026-10-14; *superseded: `ITSM_BASE_URL`* — ITSM serves its API and its pages
 * from different hosts, so the deep link has its own key), and serves the
 * result as `itsm.url`; it is status-blind for the same reason it always was.
 * This function answers *should this reader be offered it* — sync state.
 *
 * ⛔ Folding them would still leave a reader unable to tell which of the two a
 * `null` meant, and the split now also keeps the HOST out of this build
 * entirely — see `TaskItsmLink.url` in `lib/types.ts` for the stale-host
 * incident that moved it.
 *
 * ✅ **`'orphaned'` WITHHOLDS THE LINK — owner's decision, 2026-10-12.** The
 * counterpart is gone (the pull asked ITSM for it and ITSM answered 404), so
 * the link is known-dead before anybody clicks it, and **a dead deep link is
 * not a diagnostic** — it reads as broken software and sends the reader to ITSM
 * to hunt for a record that does not exist.
 *
 * ⛔ **EVERY OTHER STATUS IS LET THROUGH, including `'conflict'` and
 * `'failed'`, and widening this to "any unhealthy status" is the mistake it
 * guards against.** Reading a page overwrites nothing, so none of the reasons a
 * SYNC refuses is a reason to refuse a READER — the same ruling the backend's
 * DocuSign path takes in letting a `conflict` link through to fetch a PDF,
 * precisely because that status exists to stop a WRITE. Those are also the rows
 * somebody is most likely to be investigating, so withholding the only route to
 * the counterpart would be worst exactly where it is needed most. `'orphaned'`
 * is different in KIND, not in degree.
 *
 * ⚠️ **It is a POSITIVE test against one member, never `status !== 'synced'`.**
 * A member this build has never heard of is reachable — this client deploys
 * separately from the API — and a negative test would silently withhold the
 * link for every status added after it.
 *
 * ⛔ **The provenance chip is NOT withheld with it**, and the two are one
 * decision: see `OriginBadge` above and `TaskItsmLink` in `lib/types.ts`.
 */
function openableItsmUrl(task: Task): string | null {
  if (task.itsm?.status === 'orphaned') return null;
  /*
   * 🔴 THE SERVER'S URL, NOT ONE COMPOSED HERE — owner's ruling, 2026-10-13.
   *
   * ⚠️ *SUPERSEDED: `return itsmTaskUrl(task.itsm);`* — which built the address
   * from a host typed into `lib/itsm.ts` with no environment override, and that
   * host had gone stale (`itsm.` after the app moved to `workspace.`). Measured:
   * the old host answers `301`, so this button was working on somebody else's
   * redirect. See `TaskItsmLink.url` in `lib/types.ts` for why neither half of
   * the system could see that.
   *
   * ⛔ `?? itsmTaskUrl(task.itsm)` IS THE ONE EDIT THAT UNDOES THIS. It reads as
   * harmless resilience and it reinstates the second writer in full: a `null`
   * from the server MEANS no host is configured, so a fallback answers that with
   * a guess, which is the wrong-link hazard the server refuses by design.
   */
  return task.itsm?.url ?? null;
}

/**
 * Opens the task's ITSM counterpart in a new tab.
 *
 * ⚠️ **ONE COPY OF THE MARKUP AND OF BOTH LABELS, for three mount points** —
 * the detailed row, the compact line at `sm` and up, and the compact row's
 * expanded panel below it. Three hand-written copies would agree the day they
 * were written and drift on the first correction, and what would drift is the
 * accessible name of a link into somebody else's system.
 *
 * ⚠️ **`url` is NON-NULLABLE on purpose: the caller has already decided.**
 * Whether to offer the link at all is `openableItsmUrl`'s answer, so this
 * cannot be reached with nothing to open — which is what keeps the house rule
 * (**absent, never disabled**) structural rather than remembered. `Button`
 * refuses `disabled` on its `href` form at the type level besides.
 *
 * ⚠️ **`rel` IS DELIBERATELY NOT PASSED.** `Button` supplies
 * `rel="noopener noreferrer"` for any `target="_blank"` it is given, so a call
 * site cannot forget it; restating it here would suggest the guarantee lives at
 * the call site, and the next one would be written without it.
 *
 * ⚠️ **THE NEW-TAB ANNOUNCEMENT IS IN `aria-label`, NOT AN `sr-only` CHILD, and
 * that is forced rather than chosen.** `Button` renders `children` only on its
 * non-`iconOnly` form, so an `sr-only` span would be dropped silently — and an
 * `aria-label`, which an icon-only control needs for any accessible name at
 * all, would override the child's text even if it survived.
 *
 * ⚠️ **`title` AND `aria-label` DIFFER BY EXACTLY THE PARENTHETICAL, and that
 * is why `title` is passed at all.** `Button` falls back to the `aria-label`
 * for its tooltip when no `title` is given — as Edit and Delete beside it do —
 * and that fallback would print "(opens in a new tab)" ON SCREEN, where it
 * reads as helper text. They otherwise say the same words, so the tooltip
 * matches its neighbours' wording rather than being tersely different.
 */
function OpenInItsmButton({ title, url }: { title: string; url: string }) {
  return (
    <Button
      size="sm"
      variant="ghost"
      iconOnly
      href={url}
      target="_blank"
      title={`Open in ITSM: ${title}`}
      aria-label={`Open in ITSM: ${title} (opens in a new tab)`}
      leadingIcon={<ExternalTabIcon className="h-4 w-4" />}
    />
  );
}

/**
 * A separator for the meta line.
 *
 * Hidden from the a11y tree: the line is a run of short facts that wraps two or
 * three times at 320px, and a dot read aloud between each one is noise.
 */
function Dot() {
  return (
    <span aria-hidden="true" className="text-fg-subtle">
      &middot;
    </span>
  );
}

/* --- due time ------------------------------------------------------------ */

/**
 * When the task is due, on the ASSIGNEE's clock.
 *
 * `TaskDue` carries four fields and only `utc` is a value; `local` is already
 * a rendering in the zone of whoever has to act. `formatDue` prints that wall
 * clock and appends the zone whenever it is not the reader's own — without
 * it, a London reader sees "15:00" on a task set for Mumbai and plans their
 * afternoon around a deadline that already passed. `isForeignZone` says so in
 * words too, since a parenthesised zone is easy to skim past on a dense row.
 *
 * Overdue is never carried by colour alone: the word "Overdue" and a warning
 * glyph both appear, because a red timestamp is invisible to a meaningful
 * share of the people who own these rows.
 */
function DueLine({
  task,
  viewerTimeZone,
  now,
}: {
  task: Task;
  viewerTimeZone: string;
  /** The skew-corrected clock where the caller has one; the local clock otherwise. */
  now?: Date | undefined;
}) {
  if (task.due === null) return null;

  const overdue = !isClosed(task.state) && isOverdue(task.due, now);
  const foreign = isForeignZone(task.due, viewerTimeZone);
  const relative = relativeDue(task.due, now);
  const assigneeName = task.assignedTo?.name ?? 'the assignee';

  return (
    <span
      className={cx(
        'inline-flex min-w-0 flex-wrap items-center gap-1',
        overdue ? 'font-medium text-danger' : 'text-fg-secondary',
      )}
      {...(foreign
        ? { title: `Due on ${assigneeName}'s clock (${task.due.timeZone}), not yours.` }
        : {})}
    >
      {overdue ? <AlertIcon /> : <ClockIcon />}
      <span className="tabular">{formatDue(task.due, viewerTimeZone)}</span>
      {foreign && <span className="font-normal text-fg-muted">their time</span>}
      {overdue ? (
        <Badge tone="danger">Overdue</Badge>
      ) : (
        relative && <span className="font-normal text-fg-muted">{relative}</span>
      )}
    </span>
  );
}

/* --- one row ------------------------------------------------------------- */

function TaskRow({
  task,
  showTarget,
  onToggleState,
  onEdit,
  onDelete,
  busy,
  viewerTimeZone,
  viewerId,
  canUpdate,
  canDelete,
  writeDenialReason,
}: {
  task: Task;
  showTarget: boolean;
  onToggleState: (task: Task, next: TaskState) => void;
  onEdit: (task: Task) => void;
  onDelete: (task: Task) => void;
  busy: boolean;
  viewerTimeZone: string;
  viewerId: number | null;
  canUpdate: boolean;
  canDelete: boolean;
  writeDenialReason: string | null;
}) {
  const [confirming, setConfirming] = useState(false);
  const [showFiles, setShowFiles] = useState(false);

  /*
   * The file count. `attachmentCount` absent means an older server that does
   * not report it, so nothing renders — never a "0". A count reported back by
   * the panel after an add or remove overrides the row's figure only while
   * the row still carries the value it was taken against, so a refetched row
   * wins without an effect.
   */
  const [countOverride, setCountOverride] = useState<{
    from: number | undefined;
    count: number;
  } | null>(null);
  const attachmentCount =
    countOverride !== null && countOverride.from === task.attachmentCount
      ? countOverride.count
      : task.attachmentCount;
  const hasFiles = attachmentCount !== undefined && attachmentCount > 0;

  const closed = isClosed(task.state);
  const nextState: TaskState = closed ? 'open' : 'done';

  /*
   * Delete is offered to the CREATOR only. The assignee gets a 403 telling
   * them to close it instead: letting the person the work was handed to
   * erase it would remove the only trace it was ever asked for. An audit
   * holder may also delete, but that grant is not knowable per row here — so
   * an audit holder may not see a button they are in fact allowed to press,
   * which is the safe direction to be wrong in.
   *
   * ⚠️ **`canDelete` is a SECOND, independent narrowing and both must hold.**
   * It is `tasks.delete` from the permission set, which `/tasks` really
   * enforces — the creator rule is per-record and cannot express it, and the
   * grant is per-account and cannot express the creator rule.
   */
  const mayDelete = canDelete && viewerId !== null && task.createdBy?.id === viewerId;

  /*
   * All three people on a task are nullable, and the null is a fact rather
   * than missing data: they go null when that user is deleted, because work
   * that was owed has to survive somebody leaving. None may render blank.
   *
   * `assignedTo` is the ambiguous one — null is either "nobody has it" or
   * "the person who had it is gone". Either way the reader needs the same
   * thing: nobody is currently going to do this.
   */
  const assignee = task.assignedTo?.name ?? 'Unassigned';

  // Who asked for it, when that is not the reader — "you set this" is noise
  // on a panel of your own tasks, whereas "asked by Priya" decides whether
  // you can quietly drop it. A deleted creator still gets a phrase, not a
  // blank, for the same reason.
  const askedBy = task.createdBy
    ? task.createdBy.id === viewerId
      ? null
      : task.createdBy.name
    : 'a former colleague';

  // Where the ITSM counterpart can be opened, `null` when it should not be
  // offered. See `openableItsmUrl` for the `'orphaned'` ruling.
  const itsmUrl = openableItsmUrl(task);

  return (
    <li className="flex min-w-0 items-start gap-2.5 border-t border-border px-3 py-2 first:border-t-0 hover:bg-surface-hover">
      {/* Disabled, never hidden: this control IS the row's done/open state, so
          removing it would take the state away along with the affordance. The
          reason rides on the hover label so a bare `disabled` cannot read as a
          bug. The label sits on the Tooltip's wrapper span because a disabled
          button never receives the hover (`disabled:pointer-events-none`), so
          the row's margin and no-shrink move to that wrapper too. */}
      <Tooltip
        label={
          !canUpdate && writeDenialReason
            ? `${closed ? 'Re-open task' : 'Mark task complete'}. ${writeDenialReason}`
            : closed
              ? 'Re-open task'
              : 'Mark task complete'
        }
        className="mt-0.5 inline-flex shrink-0"
      >
      <button
        type="button"
        onClick={() => onToggleState(task, nextState)}
        disabled={busy || !canUpdate}
        aria-label={closed ? `Re-open task: ${task.title}` : `Mark task complete: ${task.title}`}
        className={cx(
          'grid h-4 w-4 shrink-0 place-items-center rounded-xs border max-sm:h-5 max-sm:w-5',
          'transition-colors duration-150 ease-out motion-reduce:transition-none',
          'disabled:pointer-events-none disabled:opacity-50',
          task.state === 'done'
            ? 'border-success bg-success text-fg-on-accent'
            : 'border-hairline-strong bg-surface text-transparent hover:border-fg-muted hover:text-fg-subtle',
        )}
      >
        <CheckIcon />
      </button>
      </Tooltip>

      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span
            className={cx(
              'min-w-0 text-sm font-medium [overflow-wrap:anywhere]',
              closed ? 'text-fg-muted line-through' : 'text-fg',
            )}
          >
            {task.title}
          </span>
          <Badge tone="neutral" variant="outline">{taskKindLabel(task.kind)}</Badge>
          {/* ⚠️ *SUPERSEDED, 2026-10-12:* "`cancelled` is only ever something a
              person chose — never described as a thing the system did." **The
              ITSM pull maps `rejected` → `cancelled`**, so a task a colleague
              rejected in another app now arrives here already cancelled, and
              nobody here chose it. The sentence is quoted rather than deleted
              because it was an instruction about WORDING, and copy written on
              its authority is now false for every ITSM-born rejection.

              What survives, and is the reason this chip is not optional: `done`
              and `cancelled` are both CLOSED and are OPPOSITE OUTCOMES, so the
              word is the only thing separating work that was delivered from
              work that was called off. The box is empty either way. */}
          {task.state === 'cancelled' && <Badge tone="warning">{taskStateLabel(task.state)}</Badge>}
          {/* Where the task came from, on the rows where that is not "here".
              ⚠️ **Unconditional, unlike the compact line's copy, and that is
              the difference between the two rows rather than an inconsistency**
              — this line is `flex-wrap`, so a further chip wraps instead of
              competing with the title for a 320px budget. The chip is the only
              thing on screen that explains an ITSM-born task whose counterpart
              has been orphaned, so it is never withheld with the link. */}
          {/* Ahead of provenance: a conflict is unresolved work, where "From
              ITSM" is just where it was born. */}
          <SyncConflictBadge itsm={task.itsm} className="inline-flex" />
          <OriginBadge origin={task.origin} className="inline-flex" />
        </div>

        {task.details && (
          <p className="mt-0.5 line-clamp-2 text-xs text-fg-secondary [overflow-wrap:anywhere]">
            {task.details}
          </p>
        )}

        <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-2xs text-fg-muted">
          {/* An undated task is ordinary. Printing "no due date" on every one
              turns a normal state into a defect, so the row says nothing
              about time at all. */}
          {task.due && (
            <>
              <DueLine task={task} viewerTimeZone={viewerTimeZone} />
              <Dot />
            </>
          )}
          <span className="min-w-0 truncate">{assignee}</span>
          {askedBy && (
            <>
              <Dot />
              <span className="min-w-0 truncate">asked by {askedBy}</span>
            </>
          )}
          {showTarget &&
            (task.target ? (
              <>
                <Dot />
                {/*
                  Unlinked when the record has no page in this app — see
                  `TARGET_PATH`. The label is identical either way, so a reader
                  still knows what the task is about; only the affordance goes.
                  Rendering a link to a route that 404s would be worse than
                  rendering none: it reads as a broken app rather than as a
                  record that is no longer browsable here.
                */}
                {TARGET_PATH[task.target.type] === null ? (
                  <span title={targetRef(task.target)}>{targetLabel(task.target)}</span>
                ) : (
                  <TextLink
                    to={`${TARGET_PATH[task.target.type]}/${task.target.id}`}
                    title={targetRef(task.target)}
                  >
                    {targetLabel(task.target)}
                  </TextLink>
                )}
                {task.enquiry ? (
                  <>
                    <Dot />
                    <EnquiryLink enquiry={task.enquiry} />
                  </>
                ) : null}
              </>
            ) : task.enquiry ? (
              <>
                <Dot />
                <EnquiryLink enquiry={task.enquiry} />
              </>
            ) : (
              <>
                <Dot />
                <span>Personal</span>
              </>
            ))}
          {(hasFiles || showFiles) && (
            <>
              <Dot />
              <Tooltip label={showFiles ? 'Hide attachments' : 'Show attachments'}>
              <button
                type="button"
                onClick={() => setShowFiles((open) => !open)}
                aria-expanded={showFiles}
                aria-label={`${showFiles ? 'Hide' : 'Show'} ${attachmentCount ?? 0} ${
                  attachmentCount === 1 ? 'attachment' : 'attachments'
                } on ${task.title}`}
                className="inline-flex items-center gap-0.5 rounded-xs text-fg-secondary hover:text-fg"
              >
                <PaperclipIcon />
                <span className="tabular">{attachmentCount ?? 0}</span>
              </button>
              </Tooltip>
            </>
          )}
        </div>

        {/* `completedBy` is stamped server-side and cleared on re-open, so
            its presence here is the record of the close, not a guess from
            `updatedAt`.

            ⚠️ **"Cancelled", not "Closed", and the branch is provably total.**
            The block is gated on `closed`, which is `state !== 'open'`, so the
            else arm is exactly `cancelled` — it says the outcome rather than
            leaving the reader to infer it from the absence of "Completed".

            ⚠️ **The whole block is ABSENT for an ITSM-born cancel**, which is
            correct rather than a gap: that arrives with no completer and no
            completion time, because nobody here closed it, and an invented
            sentence would credit somebody with an act they did not perform. */}
        {closed && (task.completedAt || task.completedBy) && (
          <p className="mt-1 text-2xs text-fg-subtle">
            {task.state === 'done' ? 'Completed' : 'Cancelled'} by{' '}
            {task.completedBy?.name ?? 'a former colleague'}
            {task.completedAt ? ` on ${formatDateTime(task.completedAt)}` : ''}
          </p>
        )}

        {showFiles && (
          <div className="mt-2 min-w-0">
            <TaskAttachmentsPanel
              taskId={task.id}
              canEdit={canUpdate}
              onCountChange={(count: number) =>
                setCountOverride({ from: task.attachmentCount, count })
              }
            />
          </div>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-0.5">
        {/* Withheld rather than disabled, matching Delete beside it: unlike the
            toggle above, a pencil carries no state, so an absent one costs the
            reader nothing they can see. The toggle's `title` already names the
            reason for the row. */}
        {canUpdate && (
          <Button
            size="sm"
            variant="ghost"
            iconOnly
            leadingIcon={<PencilIcon />}
            onClick={() => onEdit(task)}
            disabled={busy}
            aria-label={`Edit task: ${task.title}`}
          />
        )}
        {mayDelete && (
          <Button
            size="sm"
            variant="ghost"
            iconOnly
            leadingIcon={<TrashIcon />}
            onClick={() => setConfirming(true)}
            disabled={busy}
            aria-label={`Delete task: ${task.title}`}
          />
        )}
        {/* ⚠️ Gated on the TASK having an openable counterpart, never on
            `canUpdate` or `mayDelete` — a `viewer` holds neither and must still
            reach it. This cluster is always rendered here, so unlike the
            compact row there is no enclosing condition to widen; the compact
            row's own comment names that as the defect to watch for.

            It survives at every width because this row has no single-line
            budget to protect: it is `items-start` over several wrapped lines,
            which is the whole reason the compact row drops things and this one
            does not. */}
        {itsmUrl !== null && <OpenInItsmButton title={task.title} url={itsmUrl} />}
      </div>

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          onDelete(task);
        }}
        title="Delete this task?"
        description={
          <>
            <strong className="font-medium text-fg">{task.title}</strong> will be deleted for
            everyone and can't be recovered. Close it instead to keep the record.
          </>
        }
        confirmLabel="Delete task"
        loading={busy}
      />
    </li>
  );
}

/* --- one compact row ----------------------------------------------------- */

/** What the task is about — a link where the record has a page, plain text where not. */
function TargetRef({ task }: { task: Task }) {
  if (!task.target) {
    return task.enquiry ? <EnquiryLink enquiry={task.enquiry} /> : <span>Personal</span>;
  }
  const path = TARGET_PATH[task.target.type];
  // Unlinked when the record has no page in this app — see `TARGET_PATH`.
  const record =
    path === null ? (
      <span title={targetRef(task.target)}>{targetLabel(task.target)}</span>
    ) : (
      <TextLink to={`${path}/${task.target.id}`} title={targetRef(task.target)}>
        {targetLabel(task.target)}
      </TextLink>
    );
  if (!task.enquiry) return record;
  // The record first, then the enquiry it follows up.
  return (
    <>
      {record}
      {' · '}
      <EnquiryLink enquiry={task.enquiry} />
    </>
  );
}

/**
 * The one-line row the /tasks page uses (owner, 2026-10-11: "quick to
 * understand, a quick idea immediately").
 *
 * Collapsed it carries only what decides what to do next: done or not, what,
 * about which record, when, and who. Everything else — details, kind, who asked,
 * the assignee's clock, attachments, who closed it — sits behind the expand
 * control, which is the title itself.
 *
 * ⚠️ **The rules are the detailed row's, unchanged**: the toggle is disabled
 * (never hidden) with the reason on its hover label; Edit is withheld without
 * `tasks.update`; Delete is the CREATOR's alone AND needs `tasks.delete`. The
 * toggle is a sibling of the expand button, never inside it, so completing a
 * task does not expand it.
 *
 * ⚠️ **At 320px the record and the avatar drop out of the line** and appear in
 * the expanded panel instead, so the title keeps a readable width beside the
 * two 40px touch-sized actions. Nothing is lost — it moves one click away.
 *
 * ⚠️ **AND SO DO THE TWO ITSM AFFORDANCES, for that same stated reason rather
 * than by habit.** `Button`'s icon-only control is **44px on a phone** (the
 * `max-sm:` touch bump), so a third one would take the action cluster from 88px
 * to 132px of a 320px line — which is the budget the sentence above exists to
 * protect. The "From ITSM" chip costs another ~60px on the rows that carry it.
 * Both therefore render in the line at `sm` and up, and in the expanded panel
 * below it, exactly as the record reference already does. **Nothing is lost at
 * any width; below `sm` it moves one click away.**
 *
 * ⛔ **THE DEEP LINK IS COMPUTED FROM PROPS AND NEEDS NO NEW ONE.** `task.itsm`
 * carries the counterpart and `lib/itsm.ts` owns the host and path, so this
 * stays presentational — no fetch, no mutation, nothing for the two `/tasks`
 * mounts to pass differently and therefore nothing for them to diverge on.
 */
function CompactTaskRow({
  task,
  showTarget,
  onToggleState,
  onEdit,
  onDelete,
  busy,
  viewerTimeZone,
  viewerId,
  canUpdate,
  canDelete,
  writeDenialReason,
  now,
}: {
  task: Task;
  showTarget: boolean;
  onToggleState: (task: Task, next: TaskState) => void;
  onEdit: (task: Task) => void;
  onDelete: (task: Task) => void;
  busy: boolean;
  viewerTimeZone: string;
  viewerId: number | null;
  canUpdate: boolean;
  canDelete: boolean;
  writeDenialReason: string | null;
  now: Date | undefined;
}) {
  const [expanded, setExpanded] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const panelId = useId();

  const closed = isClosed(task.state);
  const nextState: TaskState = closed ? 'open' : 'done';
  // Same two narrowings as `TaskRow` — see the note there.
  const mayDelete = canDelete && viewerId !== null && task.createdBy?.id === viewerId;
  const assignee = task.assignedTo?.name ?? 'Unassigned';
  const askedBy = task.createdBy
    ? task.createdBy.id === viewerId
      ? null
      : task.createdBy.name
    : 'a former colleague';
  const chip = dueChip(task.due, {
    open: !closed,
    viewerTimeZone,
    ...(now ? { now } : {}),
    assigneeName: task.assignedTo?.name ?? null,
  });
  const availableLabel = availableFromLabel(task, now, viewerTimeZone);

  /*
   * Where the ITSM counterpart can be opened, `null` when it should not be
   * offered — see `openableItsmUrl` for the `'orphaned'` ruling, and
   * `TaskItsmLink.url` in `lib/types.ts` for why a `null` can also mean the
   * server has no ITSM host configured. *(SUPERSEDED: "`itsmTaskUrl` for the
   * three states that collapse to `null`" — that function is deleted.)*
   *
   * ⚠️ **ABSENT, NEVER DISABLED.** `null` renders nothing at all: an affordance
   * whose only outcome is a 404 is a control that lies. Same ruling as
   * `OpenTrackerSheetButton` and the Xero `sync` control.
   *
   * ⚠️ **ONE ELEMENT, RENDERED IN TWO PLACES, so the two cannot diverge** — the
   * line at `sm` and up, the expanded panel below it. Only one is ever in the
   * a11y tree, because the other's wrapper is `display: none`.
   *
   * 🔴 **ITS NULL-NESS IS A SIGNAL THREE OTHER DECISIONS READ, so this must stay
   * a nullable VALUE and the test must NOT move inside `OpenInItsmButton`.**
   * Raised 2026-10-13 as apparent duplication — `TaskRow` gates inline with
   * `{itsmUrl !== null && …}` while this builds a value — and the two are
   * different in KIND rather than two spellings of one rule. Pushing the null
   * test into the component makes `openInItsm` truthy always, and then:
   *
   *   - the action cluster renders for a reader with no actions, because
   *     `openInItsm !== null` is one of its three disjuncts (see the comment on
   *     that condition — it is what gives a `viewer` any route to ITSM at all);
   *   - an empty `<span className="hidden shrink-0 sm:inline-flex">` renders;
   *   - **the expanded panel renders its `<Dot />` separator with nothing after
   *     it** — a dangling bullet on screen below `sm`.
   *
   * ⛔ So the apparent tidy-up is a visible layout defect, and `tsc` would not
   * have said a word: a component returning `null` is perfectly assignable.
   */
  const itsmUrl = openableItsmUrl(task);
  const openInItsm =
    itsmUrl === null ? null : <OpenInItsmButton title={task.title} url={itsmUrl} />;

  return (
    <li className="min-w-0 border-t border-border first:border-t-0">
      <div className="flex min-w-0 items-center gap-2 px-3 py-1 hover:bg-surface-hover max-sm:flex-wrap">
        <Tooltip
          label={
            !canUpdate && writeDenialReason
              ? `${closed ? 'Re-open task' : 'Mark task complete'}. ${writeDenialReason}`
              : closed
                ? 'Re-open task'
                : 'Mark task complete'
          }
          className="inline-flex shrink-0"
        >
          <button
            type="button"
            onClick={() => onToggleState(task, nextState)}
            disabled={busy || !canUpdate}
            aria-label={closed ? `Re-open task: ${task.title}` : `Mark task complete: ${task.title}`}
            className={cx(
              'grid h-4 w-4 shrink-0 place-items-center rounded-xs border max-sm:h-5 max-sm:w-5',
              'transition-colors duration-150 ease-out motion-reduce:transition-none',
              'disabled:pointer-events-none disabled:opacity-50',
              task.state === 'done'
                ? 'border-success bg-success text-fg-on-accent'
                : 'border-hairline-strong bg-surface text-transparent hover:border-fg-muted hover:text-fg-subtle',
            )}
          >
            <CheckIcon />
          </button>
        </Tooltip>

        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          aria-expanded={expanded}
          aria-controls={panelId}
          className="flex min-h-8 min-w-0 flex-1 items-center gap-1.5 rounded-xs text-left max-sm:min-h-10"
        >
          <span className="shrink-0 text-fg-subtle">
            <ChevronIcon direction={expanded ? 'down' : 'right'} className="h-3 w-3" />
          </span>
          <span
            className={cx(
              'min-w-0 truncate text-sm',
              closed ? 'text-fg-muted line-through' : 'font-medium text-fg',
            )}
          >
            {task.title}
          </span>
          {/* A cancelled task is struck through like a done one but its box is
              empty, so the word is what tells the two apart.
              🔴 **AND IT GOES `sr-only` BELOW `sm` WHEN THE TASK IS ALSO IN
              CONFLICT, measured 2026-10-14.** At 320px a row carrying BOTH
              chips rendered **0px of its 247px title — the task was
              unidentifiable** — and the conflict chip overlapped the Edit
              control by 25px. Two `shrink-0` chips plus a `truncate` title do
              not fit. Hiding this one recovered **94px** and gave the title 67px
              (27%), and removed the overlap for 15px of clearance.
              ⛔ Unconditional at `sm` and above, where both fit.

              🔴 **`max-sm:sr-only` RATHER THAN `hidden`, AND THE DIFFERENCE IS
              AN ACCESSIBILITY REGRESSION I SHIPPED FOR AN HOUR.** *SUPERSEDED:
              `hidden … sm:inline-flex`, justified as "the word that separates
              done from cancelled is still in the expanded panel".* **That was
              FALSE and was measured false:** the panel renders
              `taskKindLabel`, not `taskStateLabel`, which appears at only two
              sites and both are row chips. So below 640px the word was
              reachable **nowhere** — not the row, not the panel, not the
              accessibility tree.
              ⛔ **And `isClosed` is `state !== 'open'`, so the checkbox reads
              "Re-open task: …" for a DONE task and a CANCELLED one alike** —
              measured, a cancelled row and a done row were textually identical
              apart from the title, separated only by checkbox colour, and for a
              screen-reader user by NOTHING.
              ✅ `sr-only` is out of flow, so it costs the row no width while
              keeping the word in the accessible name at every width — the same
              pattern `BuildSwitcher` uses. **The width is recovered AND the
              distinction survives.** */}
          {task.state === 'cancelled' && (
            /* ⚠️ THE RESPONSIVE DISPLAY CLASS GOES ON A WRAPPER, NEVER ON
               `Badge`. `Badge` bakes in `inline-flex`, so `hidden
               sm:inline-flex` handed to it would put two display utilities on
               one element and let the emitted stylesheet's order decide which
               wins — the exact race `OriginBadge` uses a wrapper to avoid, and
               the reason `Badge.wrap` is a prop rather than a class. A first
               draft of this did pass it straight to `Badge`. */
            <span
              className={
                task.itsm?.status === 'conflict' ? 'shrink-0 max-sm:sr-only' : 'shrink-0'
              }
            >
              <Badge tone="warning">{taskStateLabel(task.state)}</Badge>
            </span>
          )}
          {/* Where the task came from, on the rows where that is not "here".
              Hidden below `sm` and shown in the expanded panel instead, with
              the record reference and the avatar and for the same reason — see
              this row's header for the 320px budget. */}
          {/* ⚠️ Shown at EVERY width, unlike the provenance chip beside it. That
              chip is hidden below `sm` to protect the 320px budget because its
              fact is the same on most rows; a conflict is rare, actionable and
              freezes the task's sync, so it earns the width where it appears. */}
          <SyncConflictBadge itsm={task.itsm} className="inline-flex shrink-0" />
          <OriginBadge origin={task.origin} className="hidden shrink-0 sm:inline-flex" />
          {/* A snoozed follow-up (only listed with `includeUpcoming`): the
              server hides it until `availableFrom`, so say when it returns. */}
          {availableLabel && (
            <Badge tone="neutral" className="shrink-0">
              {`From ${availableLabel}`}
            </Badge>
          )}
        </button>

        {showTarget && (
          <span className="hidden min-w-0 max-w-[14rem] shrink truncate text-xs text-fg-secondary sm:inline">
            <TargetRef task={task} />
          </span>
        )}

        {/* The full date is long, so below `sm` it drops to its own line under
            the title (order-last, full width, indented past the checkbox and
            chevron) rather than squeezing the title to nothing — measured at
            320, where beside the title it left the title 0px wide. Segments
            never break mid-date. */}
        {chip && (
          <span
            title={chip.title}
            className={cx(
              'inline-flex shrink-0 flex-wrap items-center justify-end gap-x-1 text-right text-xs tabular',
              'max-sm:order-last max-sm:w-full max-sm:justify-start max-sm:pb-1 max-sm:pl-[2.875rem] max-sm:text-left',
              chip.late ? 'font-medium text-danger' : 'text-fg-secondary',
            )}
          >
            {chip.late && <AlertIcon />}
            <span className="whitespace-nowrap">
              {chip.time ? `${chip.date},` : chip.date}
            </span>
            {chip.time && <span className="whitespace-nowrap">{chip.time}</span>}
            {chip.lateText && <span className="whitespace-nowrap">{`· ${chip.lateText}`}</span>}
          </span>
        )}

        <Tooltip label={assignee} describes={false} className="hidden shrink-0 sm:inline-flex">
          <span className="inline-flex">
            <Avatar name={task.assignedTo?.name ?? '?'} size="sm" />
            <span className="sr-only">{`Assigned to ${assignee}`}</span>
          </span>
        </Tooltip>

        {/* ⚠️ `openInItsm` is in this condition, and leaving it out is the
            defect to watch for: the ITSM link is gated on the TASK having a
            counterpart, not on the reader holding `tasks.update` or being the
            creator. Without it, a `viewer` — who holds neither — gets no
            cluster at all and no way to reach the counterpart. */}
        {(canUpdate || mayDelete || openInItsm !== null) && (
          <div className="flex shrink-0 items-center">
            {canUpdate && (
              <Button
                size="sm"
                variant="ghost"
                iconOnly
                leadingIcon={<PencilIcon />}
                onClick={() => onEdit(task)}
                disabled={busy}
                aria-label={`Edit task: ${task.title}`}
              />
            )}
            {mayDelete && (
              <Button
                size="sm"
                variant="ghost"
                iconOnly
                leadingIcon={<TrashIcon />}
                onClick={() => setConfirming(true)}
                disabled={busy}
                aria-label={`Delete task: ${task.title}`}
              />
            )}
            {/* The wrapper carries the breakpoint rather than the `Button`,
                which bakes in `inline-flex` — see `OriginBadge` for the same
                reason. Below `sm` this renders in the expanded panel. */}
            {openInItsm !== null && (
              <span className="hidden shrink-0 sm:inline-flex">{openInItsm}</span>
            )}
          </div>
        )}
      </div>

      {/* Mounted only while open: the attachments panel fetches on mount, and a
          list of fifty rows must not issue fifty requests nobody asked for. */}
      <div id={panelId} hidden={!expanded}>
        {expanded && (
          <div className="grid min-w-0 gap-1.5 pb-3 pl-9 pr-3 max-sm:pl-10">
            {task.details && (
              <p className="m-0 whitespace-pre-wrap text-xs text-fg-secondary [overflow-wrap:anywhere]">
                {task.details}
              </p>
            )}
            <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 text-2xs text-fg-muted">
              <Badge tone="neutral" variant="outline">
                {taskKindLabel(task.kind)}
              </Badge>
              {task.due && (
                <>
                  <Dot />
                  <DueLine task={task} viewerTimeZone={viewerTimeZone} now={now} />
                </>
              )}
              <Dot />
              <span className="min-w-0">{assignee}</span>
              {askedBy && (
                <>
                  <Dot />
                  <span className="min-w-0">asked by {askedBy}</span>
                </>
              )}
              {showTarget && (
                <span className="inline-flex min-w-0 items-center gap-1.5 sm:hidden">
                  <Dot />
                  <TargetRef task={task} />
                </span>
              )}
              {/* Both ITSM affordances, below `sm` only — where the line drops
                  them for the width budget in this row's header. `sm:hidden` on
                  each, so neither is ever shown twice.
                  ⛔ **`SyncConflictBadge` IS DELIBERATELY NOT HERE, and its
                  absence is not an omission.** This block exists to re-show what
                  the compact row HIDES below `sm`; the conflict chip is shown at
                  EVERY width on that row, so a copy here would be a second
                  mention on exactly the screens it already appears on. Add it
                  only if that row ever starts hiding it. */}
              <OriginBadge origin={task.origin} className="inline-flex sm:hidden" />
              {openInItsm !== null && (
                <span className="inline-flex items-center gap-1.5 sm:hidden">
                  <Dot />
                  {openInItsm}
                </span>
              )}
            </div>
            {/* "Cancelled" rather than "Closed", and absent entirely for an
                ITSM-born cancel — see the detailed row for both reasons. */}
            {closed && (task.completedAt || task.completedBy) && (
              <p className="m-0 text-2xs text-fg-subtle">
                {task.state === 'done' ? 'Completed' : 'Cancelled'} by{' '}
                {task.completedBy?.name ?? 'a former colleague'}
                {task.completedAt ? ` on ${formatDateTime(task.completedAt)}` : ''}
              </p>
            )}
            <div className="min-w-0">
              <TaskAttachmentsPanel taskId={task.id} canEdit={canUpdate} />
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          onDelete(task);
        }}
        title="Delete this task?"
        description={
          <>
            <strong className="font-medium text-fg">{task.title}</strong> will be deleted for
            everyone and can't be recovered. Close it instead to keep the record.
          </>
        }
        confirmLabel="Delete task"
        loading={busy}
      />
    </li>
  );
}

/* --- states -------------------------------------------------------------- */

/**
 * The loading state, in the shape of the rows it becomes.
 *
 * Same row height, same checkbox, same two lines of text — a skeleton that
 * doesn't match its content makes the panel jump when the data lands, which
 * reads as a bug even when nothing is wrong.
 */
export function TaskListSkeleton({ rows = 3 }: { rows?: number }) {
  return (
    <ul className="min-w-0">
      {Array.from({ length: rows }, (_unused, index) => (
        <li
          key={index}
          className="flex min-w-0 items-start gap-2.5 border-t border-border px-3 py-2 first:border-t-0"
        >
          <Skeleton className="mt-0.5 h-4 w-4 rounded-xs" />
          <div className="min-w-0 flex-1">
            <Skeleton className="h-4 w-1/2" />
            <Skeleton className="mt-1.5 h-3 w-3/4" />
          </div>
        </li>
      ))}
    </ul>
  );
}

/* --- the list ------------------------------------------------------------ */

export interface TaskListProps {
  tasks: Task[];
  /** Hides the record chip when every task is already on that record's page. */
  showTarget: boolean;
  /** Optimistic completion toggle. */
  onToggleState: (task: Task, next: TaskState) => void;
  onEdit: (task: Task) => void;
  onDelete: (task: Task) => void;
  busyTaskId: number | null;
  /** The signed-in user's IANA zone, for rendering due times. */
  viewerTimeZone: string;
  /** The signed-in user's id — decides whether Delete is even offered. */
  viewerId: number | null;
  /**
   * `tasks.update` and `tasks.delete` from the caller's `ability`.
   *
   * ⚠️ **REQUIRED, and deliberately not defaulted to `true`.** `tasks` is one
   * of the three modules the API really enforces through permission sets, so a
   * mount that forgets these would offer writes the server refuses — exactly
   * the defect these props were added to close. A default would make that
   * mount compile silently; `tsc` naming it is the whole guard.
   *
   * ⚠️ **They are ANDed with the per-record rules, never a replacement for
   * them.** Delete is still the CREATOR's alone below; this only narrows.
   */
  canUpdate: boolean;
  canDelete: boolean;
  /**
   * Why the completion toggle is disabled, when it is — `null` when it is not.
   *
   * The toggle is disabled rather than hidden because it IS the row's state
   * indicator: removing it would take away the only thing saying whether the
   * task is done. A bare `disabled` on it would make a permission denial
   * indistinguishable from a row that is merely busy, so the reason rides
   * along. Composed by the caller through `denialReason`, so the wording has
   * one home.
   */
  writeDenialReason?: string | null;
  /**
   * Optional, so the props above remain the whole contract for a caller that
   * already holds its rows. The list owns the loading and error renderings
   * because every surface that shows tasks would otherwise write its own, and
   * they would drift apart.
   */
  loading?: boolean;
  /** Rendered instead of the rows when the fetch that produced them failed. */
  error?: ReactNode;
  /** Offered from the empty state, when the caller has somewhere to send them. */
  emptyAction?: ReactNode;
  /**
   * `'compact'` renders one line per task with the rest behind an expand
   * control — the /tasks page. Defaults to `'detailed'`, today's multi-line row,
   * so every other mount (the record activity drawer) is unchanged.
   */
  variant?: 'detailed' | 'compact';
  /** The skew-corrected clock, where the caller measured one. Compact rows only. */
  now?: Date | undefined;
}

export function TaskList({
  tasks,
  showTarget,
  onToggleState,
  onEdit,
  onDelete,
  busyTaskId,
  viewerTimeZone,
  viewerId,
  canUpdate,
  canDelete,
  writeDenialReason = null,
  loading = false,
  error,
  emptyAction,
  variant = 'detailed',
  now,
}: TaskListProps): ReactElement {
  if (loading) return <TaskListSkeleton />;

  if (error) {
    return (
      <Notice tone="danger" role="alert" className="m-3 [overflow-wrap:anywhere]">
        <span className="min-w-0">{error}</span>
      </Notice>
    );
  }

  if (tasks.length === 0) {
    return (
      <EmptyState
        icon={<TaskIcon />}
        title="No tasks"
        description="Add one to hand work to somebody."
        {...(emptyAction ? { action: emptyAction } : {})}
      />
    );
  }

  if (variant === 'compact') {
    return (
      <ul className="min-w-0">
        {tasks.map((task) => (
          <CompactTaskRow
            key={task.id}
            task={task}
            showTarget={showTarget}
            onToggleState={onToggleState}
            onEdit={onEdit}
            onDelete={onDelete}
            busy={busyTaskId === task.id}
            viewerTimeZone={viewerTimeZone}
            viewerId={viewerId}
            canUpdate={canUpdate}
            canDelete={canDelete}
            writeDenialReason={writeDenialReason}
            now={now}
          />
        ))}
      </ul>
    );
  }

  return (
    <ul className="min-w-0">
      {tasks.map((task) => (
        <TaskRow
          key={task.id}
          task={task}
          showTarget={showTarget}
          onToggleState={onToggleState}
          onEdit={onEdit}
          onDelete={onDelete}
          busy={busyTaskId === task.id}
          viewerTimeZone={viewerTimeZone}
          viewerId={viewerId}
          canUpdate={canUpdate}
          canDelete={canDelete}
          writeDenialReason={writeDenialReason}
        />
      ))}
    </ul>
  );
}
